import { FetchResult } from "@apollo/client";
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Typography,
} from "@mui/material";
import { ReactElement } from "react";
import { FormattedMessage } from "react-intl";

import {
  FormikDialog,
  FormikSelect,
  FormikSubmitButton,
} from "~frontend/components/forms";
import FormikErrorBanner, {
  submitErrorField,
} from "~frontend/components/forms/FormikErrorBanner";
import theme from "~frontend/theme";
import { useErrorMessageFormatter } from "~frontend/util/errors";

type EscalateOption = { inboxName: string; inboxId: string };

export function MoveEscalationDialog({
  isOpen,
  inboxName,
  escalateOptions,
  handleEscalate,
  handleClose,
}: {
  isOpen: boolean;
  inboxName: string;
  escalateOptions: EscalateOption[];
  handleEscalate: (inboxId: string) => Promise<FetchResult<unknown>>;
  handleClose: () => void;
}): ReactElement {
  const getErrorMessage = useErrorMessageFormatter();
  return (
    <FormikDialog<{
      escalationReason: string | null;
      [submitErrorField]: unknown;
    }>
      open={isOpen}
      initialValues={{
        escalationReason: null,
        [submitErrorField]: null,
      }}
      onSubmit={async (values, formik) => {
        try {
          const result = await handleEscalate(values.escalationReason!);
          if (result.errors) {
            formik.setErrors({
              [submitErrorField]: `Failed to move escalation: ${result.errors[0].message}`,
            });
          } else {
            handleClose();
          }
        } catch (e: unknown) {
          formik.setErrors({
            [submitErrorField]: getErrorMessage(e),
          });
        }
      }}
    >
      {({ values }) => (
        <>
          <DialogTitle>
            <FormattedMessage
              defaultMessage="Move this conversation out of the {inboxName} escalation inbox?"
              description="dialog title for moving an escalation"
              values={{ inboxName }}
            />
          </DialogTitle>
          <DialogContent>
            <Typography>
              <FormattedMessage
                defaultMessage="If this thread is in the wrong escalation inbox, you can change the escalation reason below to move it to the correct inbox."
                description="Instructions for moving a thread to a different escalation inbox"
              />
            </Typography>
            <FormikErrorBanner />
            <FormikSelect
              name="escalationReason"
              fullWidth
              label={
                <FormattedMessage
                  defaultMessage="Change Escalation Reason"
                  description="Escalation reason selector label"
                />
              }
            >
              {escalateOptions
                .filter(option => option.inboxName !== inboxName)
                .map(({ inboxName, inboxId }) => (
                  <MenuItem key={inboxId} value={inboxId}>
                    {inboxName}
                  </MenuItem>
                ))}
            </FormikSelect>
          </DialogContent>
          <DialogActions sx={{ padding: theme.spacing(1) }}>
            <Button disabled={false} onClick={handleClose}>
              <FormattedMessage
                defaultMessage="Cancel"
                description="Cancel button"
              />
            </Button>
            <FormikSubmitButton
              disabled={!values.escalationReason}
              variant="contained"
              submitText={
                <FormattedMessage
                  defaultMessage="Move Conversation"
                  description="Submit button text for moving an escalation"
                />
              }
            />
          </DialogActions>
        </>
      )}
    </FormikDialog>
  );
}
